import { create } from 'zustand';
import { DomainGroup, PageItem } from '@/types/dashboard';
import { projectApi, analyticsApi } from '@/api/dashboard';
import { transformProjects } from '@/lib/projectTransform';
import { normalizeLegacyPlatformHost, normalizeLegacyPlatformUrl } from '@/lib/platformDomain';
import { buildPublishedPageUrl } from '@/lib/publishedPageUrl';
import {
  getDashboardCounts,
  getTrackingErrorCode,
  trackDashboardDataLoadResult,
} from '@/lib/dashboardTracking';

const getDomainFromUrl = (url?: string) => {
  if (!url) return '';
  try {
    return normalizeLegacyPlatformHost(new URL(url).host);
  } catch {
    return '';
  }
};

/**
 * DashboardState - 仪表盘状态接口定义
 * 承载了仪表盘页面的核心数据模型、统计汇总信息以及 UI 交互状态。
 */
interface DashboardState {
  // --- 核心业务数据 ---
  domainGroups: DomainGroup[];
  drafts: PageItem[];
  isLoading: boolean;
  error: string | null;

  // --- 统计概览数据 ---
  pageCount: number;
  domainCount: number;
  liveCount: number;

  // --- 发布后 URL 补丁（后端列表可能延迟，独立存储以跨 fetchProjects 保留） ---
  /** projectId -> url，由 patchProjectUrl 写入，fetchProjects 读取后合并 */
  _patchedUrls: Record<string, string>;
  /** 当前选择的时间范围过滤（如 'today', '7d', 'all'） */
  timeRange: string;
  /** “添加页面”对话框的打开状态 */
  isAddPanelOpen: boolean;
  /** 正在为哪个域名添加页面（如果是从域名行触发） */
  activeAddDomain: string | null;
  activeAddSiteId: number | null;
  /** 当前选定域名下已存在的页面标题（用于前端校验或展示重复） */
  activeExistingPages: string[];
  /** 当前选定项目关联的最新会话 ID */
  activeSessionId: string | null;
  activePreferredBlueprintId: string | null;

  // --- Actions ---
  /**
   * 异步刷新仪表盘数据
   * 调用接口获取原始项目，经过转换逻辑填充 domainGroups 和 drafts，并同步更新统计数值。
   */
  fetchProjects: () => Promise<void>;
  /**
   * 直接更新某个项目的 URL（发布后后端列表数据可能延迟，用此方法立即补充）
   */
  patchProjectUrl: (projectId: string, url: string) => void;
  /**
   * 设置时间过滤范围
   * @param range - 时间范围字符串
   */
  setTimeRange: (range: string) => void;
  /**
   * 打开添加面板并注入上下文
   * @param domain - 可选的目标域名
   * @param existingPages - 该域名下已有的页面标题列表
   * @param sessionId - 该域名下关联的项目会话 ID
   */
  openAddPanel: (context?: {
    domain?: string;
    siteId?: number | null;
    existingPages?: string[];
    sessionId?: string | null;
    preferredBlueprintId?: string | null;
  }) => void;
  /**
   * 关闭添加页面对话框并清除注入的上下文
   */
  closeAddPanel: () => void;
}

/**
 * useDashboardStore - 仪表盘业务状态中心 (Zustand)
 * 整合了数据获取、格式转换（VO -> UI Model）、统计聚合以及复杂弹窗的交互逻辑。
 * 是仪表盘页面的核心数据源。
 */
export const useDashboardStore = create<DashboardState>((set) => ({
  // --- 初始状态 ---
  domainGroups: [],
  drafts: [],
  isLoading: true,
  error: null,

  pageCount: 0,
  domainCount: 0,
  liveCount: 0,

  _patchedUrls: {},

  timeRange: 'today',
  isAddPanelOpen: false,
  activeAddDomain: null,
  activeAddSiteId: null,
  activeExistingPages: [],
  activeSessionId: null,
  activePreferredBlueprintId: null,

  /**
   * 实现：获取并处理项目列表数据
   */
  fetchProjects: async () => {
    set({ isLoading: true, error: null });
    const startedAt = performance.now();
    try {
      // 1. 调用 API 获取原始数据
      const projects = await projectApi.getProjectList();

      // 2. 数据结构转换（后端拍平的数据 -> 前端分组后的结构）
      const { domainGroups } = transformProjects(projects);
      const { drafts } = transformProjects(projects);
      // drafts = drafts.reverse().slice(0, 10);

      // 3. 保留之前通过 patchProjectUrl 设置的 URL（后端列表可能延迟更新）
      const prevState = useDashboardStore.getState();
      // 合并三个来源：localStorage 持久化的 live URL > 内存补丁表 > 当前页面数组中已有的 URL
      const storedUrls: Record<string, string> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('pub_url_')) {
          const pid = key.replace('pub_url_', '');
          storedUrls[pid] = normalizeLegacyPlatformUrl(localStorage.getItem(key) || '');
        }
      }
      const mergedUrlMap: Record<string, string> = { ...storedUrls, ...prevState._patchedUrls };
      [...prevState.drafts, ...prevState.domainGroups.flatMap((g) => g.pages)].forEach((p) => {
        if (p.url && !mergedUrlMap[p.id]) mergedUrlMap[p.id] = p.url;
      });
      // 先使用当前项目 sitePath 能推导出的已绑定域名，其次再回退到已缓存 live URL。
      const applyPrevUrl = (pages: PageItem[]) =>
        pages.map((p) => {
          const derivedUrl = buildPublishedPageUrl({ sitePath: p.sitePaths?.[0] });
          const saved = mergedUrlMap[p.id];
          const resolvedUrl = derivedUrl || saved || p.url || '';
          if (resolvedUrl) {
            return { ...p, url: normalizeLegacyPlatformUrl(resolvedUrl), status: 'live' as const };
          }
          return p;
        });

      // 4. 统计计算
      const { domainCount, draftCount, liveCount, pageCount } = getDashboardCounts(
        domainGroups,
        drafts,
      );
      const trackProjectListSuccess = () =>
        trackDashboardDataLoadResult({
          result: 'success',
          source: 'project_list',
          domainCount,
          pageCount,
          draftCount,
          liveCount,
          durationMs: Math.round(performance.now() - startedAt),
          errorCode: null,
        });

      // 5. 先更新项目列表（不等统计数据）
      set({
        domainGroups: domainGroups.map((g) => ({ ...g, pages: applyPrevUrl(g.pages) })),
        drafts: applyPrevUrl(drafts),
        domainCount,
        liveCount,
        pageCount,
        isLoading: false,
      });

      // 6. 批量拉取统计数据（所有已发布项目的 tenantId）
      const allPages = [...domainGroups.flatMap((g) => g.pages), ...drafts];
      const tenantIds = [
        ...new Set(allPages.map((p) => p.tenantId).filter((id): id is number => !!id)),
      ];

      if (tenantIds.length === 0) {
        trackProjectListSuccess();
        return;
      }

      const now = new Date();
      const endTime = now.toISOString();
      const startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const compareEnd = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        23,
        59,
        59,
      ).toISOString();
      const compareStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
      ).toISOString();

      const batchResult = await analyticsApi.batchQueryEventStatsSummary({
        startTime,
        endTime,
        compareStartTime: compareStart,
        compareEndTime: compareEnd,
        tenantIds,
      });

      // 构建 tenantId -> stats 的映射
      const statsMap = new Map<
        number,
        {
          sessionCount: number;
          conversionCount: number;
          conversionRate: number;
          sessionCountChangeRate: number;
          conversionCountChangeRate: number;
          conversionRateDiff: number;
        }
      >();
      for (const group of batchResult) {
        // 聚合该租户下所有事件的数据（sessionCount 取最大值避免重复计数，其余累加）
        let sessionCount = 0;
        let conversionCount = 0;
        let conversionRate = 0;
        let sessionCountChangeRate = 0;
        let conversionCountChangeRate = 0;
        let conversionRateDiff = 0;
        let hasConversion = false;

        for (const item of group.items) {
          if (Number(item.sessionCount ?? 0) > sessionCount) {
            sessionCount = Number(item.sessionCount ?? 0);
          }
          if (item.conversionEvent) {
            conversionCount += item.conversionCount ?? 0;
            conversionRate = item.conversionRate ?? 0;
            conversionCountChangeRate = item.conversionCountChangeRate ?? 0;
            conversionRateDiff = item.conversionRateDiff ?? 0;
            hasConversion = true;
          }
          sessionCountChangeRate = item.sessionCountChangeRate ?? 0;
        }
        // 如果没有转化事件，计算率取第一个事件的
        if (!hasConversion && group.items.length > 0) {
          conversionRate = group.items[0].conversionRate ?? 0;
          conversionRateDiff = group.items[0].conversionRateDiff ?? 0;
        }

        statsMap.set(group.tenantId, {
          sessionCount,
          conversionCount,
          conversionRate,
          sessionCountChangeRate,
          conversionCountChangeRate,
          conversionRateDiff,
        });
      }

      // 将统计数据合并到 pages
      const applyStats = (pages: PageItem[]) =>
        pages.map((p) => {
          if (!p.tenantId) return p;
          const s = statsMap.get(p.tenantId);
          if (!s) return p;
          return {
            ...p,
            stats: {
              conversion: `${s.conversionRate.toFixed(1)}%`,
              sessions: String(s.sessionCount),
              leads: String(s.conversionCount),
              delta: {
                conversion: `${s.conversionRateDiff >= 0 ? '+' : ''}${s.conversionRateDiff.toFixed(2)}pp`,
                sessions: `${s.sessionCountChangeRate >= 0 ? '+' : ''}${s.sessionCountChangeRate.toFixed(1)}%`,
                leads: `${s.conversionCountChangeRate >= 0 ? '+' : ''}${s.conversionCountChangeRate.toFixed(1)}%`,
                isUp: {
                  conversion: s.conversionRateDiff >= 0,
                  sessions: s.sessionCountChangeRate >= 0,
                  leads: s.conversionCountChangeRate >= 0,
                },
              },
            },
          };
        });

      const updatedGroups = domainGroups.map((g) => ({
        ...g,
        pages: applyStats(applyPrevUrl(g.pages)),
      }));
      const updatedDrafts = applyStats(applyPrevUrl(drafts));
      set({ domainGroups: updatedGroups, drafts: updatedDrafts });
      trackProjectListSuccess();
    } catch (err: unknown) {
      console.error('Failed to fetch dashboard projects:', err);
      const message = err instanceof Error ? err.message : '获取项目失败';
      set({
        error: message,
        isLoading: false,
        domainGroups: [],
        drafts: [],
      });
      trackDashboardDataLoadResult({
        result: 'failed',
        source: 'project_list',
        domainCount: 0,
        pageCount: 0,
        draftCount: 0,
        liveCount: 0,
        durationMs: Math.round(performance.now() - startedAt),
        errorCode: getTrackingErrorCode(err),
      });
    }
  },

  /**
   * 实现：更新时间过滤
   * @param timeRange - 选中的时间段
   */
  setTimeRange: (timeRange) => set({ timeRange }),

  patchProjectUrl: (projectId, url) =>
    set((state) => {
      const normalizedUrl = normalizeLegacyPlatformUrl(url);
      const patchPages = (pages: typeof state.drafts) =>
        pages.map((p) =>
          p.id === projectId ? { ...p, url: normalizedUrl, status: 'live' as const } : p,
        );
      return {
        _patchedUrls: { ...state._patchedUrls, [projectId]: normalizedUrl },
        drafts: patchPages(state.drafts),
        domainGroups: state.domainGroups.map((g) => {
          const pages = patchPages(g.pages);
          const nextDomain = pages.find((p) => p.id === projectId)?.url
            ? getDomainFromUrl(pages.find((p) => p.id === projectId)?.url)
            : '';
          return { ...g, domain: nextDomain || g.domain, pages };
        }),
      };
    }),

  /**
   * 实现：开启面板并记录当前操作的域名上下文
   */
  openAddPanel: (context = {}) =>
    set({
      isAddPanelOpen: true,
      activeAddDomain: context.domain || null,
      activeAddSiteId:
        typeof context.siteId === 'number' && Number.isFinite(context.siteId) && context.siteId > 0
          ? context.siteId
          : null,
      activeExistingPages: context.existingPages || [],
      activeSessionId: context.sessionId || null,
      activePreferredBlueprintId: context.preferredBlueprintId || null,
    }),

  /**
   * 实现：清理并关闭面板
   */
  closeAddPanel: () =>
    set({
      isAddPanelOpen: false,
      activeAddDomain: null,
      activeAddSiteId: null,
      activeExistingPages: [],
      activeSessionId: null,
      activePreferredBlueprintId: null,
    }),
}));
